import React, { useEffect, useState, useContext } from 'react';
import AdminSidebar from '../../../components/AdminSidebar/AdminSidebar';
import { AuthContext } from '../../../context/AuthContext';
import { FaTrashAlt, FaStar, FaSearch, FaCalendarAlt, FaUser } from 'react-icons/fa';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

const AdminReviews = () => {
  const { authToken } = useContext(AuthContext);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const fetchReviews = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/reviews`);
      if (!response.ok) throw new Error('Error al cargar valoraciones del servidor.');
      const data = await response.json();
      setReviews(data.reviews || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const handleDelete = async (reviewId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta valoración?')) return;
    setDeletingId(reviewId);
    try {
      const response = await fetch(`${API_BASE_URL}/reviews/${reviewId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'No se pudo eliminar la valoración.');
      }
      setReviews(reviews.filter(r => r.id !== reviewId));
    } catch (err) {
      alert(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const filteredReviews = reviews.filter(review => {
    return (
      (review.username || '').toLowerCase().includes(search.toLowerCase()) ||
      (review.comment || '').toLowerCase().includes(search.toLowerCase()) ||
      (review.discord_id || '').includes(search)
    );
  });

  return (
    <div className="admin-users-container">
      <AdminSidebar />
      <div className="admin-content">
        <h1 className="admin-page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <FaStar style={{ color: '#ffd700' }} /> Moderación de Valoraciones
        </h1>

        {/* Buscador */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1', minWidth: '280px' }}>
            <FaSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.3)' }} />
            <input
              type="text"
              placeholder="Buscar por usuario, comentario o Discord ID..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 15px 10px 38px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.1)',
                background: 'rgba(255,255,255,0.05)',
                color: '#fff',
                fontSize: '0.95rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <button 
            onClick={fetchReviews} 
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: '1px solid #ffd700',
              background: 'transparent',
              color: '#ffd700',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Actualizar
          </button>
        </div>

        {loading && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '40px 0' }}>
            <div className="modern-spinner" />
            <span style={{ color: '#888', fontWeight: 500, marginTop: 12 }}>Cargando valoraciones del servidor...</span>
          </div>
        )}

        {error && <p className="error-message">{error}</p>}

        {!loading && !error && filteredReviews.length === 0 && (
          <div style={{ textAlign: 'center', color: '#888', padding: '40px 0', background: 'rgba(255,255,255,0.02)', borderRadius: '12px' }}>
            No se encontraron valoraciones enviadas.
          </div>
        )}

        {!loading && !error && filteredReviews.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {filteredReviews.map(review => {
              const avatarUrl = review.avatar 
                ? `https://cdn.discordapp.com/avatars/${review.discord_id}/${review.avatar}.png?size=128` 
                : 'https://cdn.discordapp.com/embed/avatars/0.png';

              return (
                <div key={review.id} style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '12px',
                  padding: '20px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '15px',
                  position: 'relative'
                }}>
                  {/* Avatar */}
                  <img
                    src={avatarUrl}
                    alt={review.username}
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      border: '2px solid rgba(255,255,255,0.1)',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      e.target.src = 'https://cdn.discordapp.com/embed/avatars/0.png';
                    }}
                  />

                  {/* Detalles de la valoración */}
                  <div style={{ flex: '1', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                      <span style={{ fontSize: '0.95rem', fontWeight: '700', color: '#fff' }}>
                        {review.username} <span style={{ fontSize: '0.8rem', color: '#666', fontWeight: '400' }}>({review.discord_id})</span>
                      </span>

                      {/* Estrellas */}
                      <div style={{ display: 'flex', gap: '2px' }}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <FaStar
                            key={star}
                            style={{
                              color: star <= review.rating ? '#ffd700' : 'rgba(255,255,255,0.1)',
                              fontSize: '0.95rem'
                            }}
                          />
                        ))}
                      </div>
                    </div>

                    <p style={{
                      color: '#ddd',
                      fontSize: '0.92rem',
                      margin: '5px 0',
                      lineHeight: '1.5',
                      fontStyle: review.comment ? 'normal' : 'italic'
                    }}>
                      {review.comment || 'Sin comentarios.'}
                    </p>

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      fontSize: '0.78rem',
                      color: '#666',
                      marginTop: '5px'
                    }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <FaCalendarAlt size={10} />
                        <span>{new Date(review.created_at).toLocaleString('es-ES')}</span>
                      </span>
                      <span>•</span>
                      <span>ID Valoración: {review.id}</span>
                    </div>
                  </div>

                  {/* Botón de Borrado */}
                  <button
                    onClick={() => handleDelete(review.id)}
                    disabled={deletingId === review.id}
                    style={{
                      background: 'rgba(219, 68, 85, 0.1)',
                      border: '1px solid rgba(219, 68, 85, 0.3)',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      color: '#db4455',
                      cursor: deletingId === review.id ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '0.82rem',
                      fontWeight: '600',
                      transition: 'all 0.2s',
                      alignSelf: 'center'
                    }}
                    title="Eliminar Reseña"
                  >
                    <FaTrashAlt size={12} />
                    <span>Eliminar</span>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReviews;
